import React, { useState } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import {
    Button,
    emitNotification,
    InformationModal,
    Tooltip,
} from "@impero-com/ui";
import { Typography } from "antd";

import {
    LegacyConfirmationControlViewModel,
    LegacyControl,
} from "api/legacy/model";
import { Footer } from "components/modals/Footer";
import { StickyFooter } from "components/stickyFooter/footer";
import { LegacyRoute } from "legacyRoutes";
import { buildControlProgramViewUrl } from "org/control/controlProgram/urlUtils";

import { OrgApplicationBar } from "../../../components/applicationBar/orgApplicationBar";
import { useControlProgramsQuery } from "../queries/useControlProgramsQuery";

import { CONTROL_ANCHORS, useControlViewAnchorLabels } from "./consts";
import { ControlViewFormState } from "./formData";
import { useLegacyControlConfirmationDataMutation } from "./queries/useLegacyControlConfirmationDataMutation";
import { useLegacyControlPreviewMutation } from "./queries/useLegacyControlPreviewMutation";
import * as theme from "./theme.css";
import {
    toReducedLegacyControl,
    toReducedLegacyControlForConfirmationData,
} from "./utils/legacyFormat";
const { Text, Paragraph } = Typography;

interface ControlCancelButtonProps {
    shouldWarn: boolean;
    onCancel: () => void;
}
function ControlCancelButton({
    shouldWarn,
    onCancel,
}: ControlCancelButtonProps) {
    const { t } = useTranslation();
    const [dialogOpen, setDialogOpen] = useState(false);

    const handleClick = (event: React.MouseEvent) => {
        if (shouldWarn) {
            event.preventDefault();
            setDialogOpen(true);
        } else {
            onCancel();
        }
    };

    return (
        <>
            <Button onClick={handleClick} size="large" variant="default">
                {t("Cancel")}
            </Button>
            <InformationModal
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                type="error"
                icon="unsavedChanges"
                title={t("Dialog_DiscardChanges_Title")}
                footer={
                    <Footer
                        onClose={() => setDialogOpen(false)}
                        onConfirm={onCancel}
                        confirmLabel={t("Discard")}
                        cancelLabel={t("KeepEditing")}
                        destructive
                    />
                }
            >
                {t("Collect_ConfirmCancel_Body")}
            </InformationModal>
        </>
    );
}

// Once control preview is ported, this should open a popup or similar
// Having a separate route for preview does not make sense as URLs should not be dependant on form state in another tab
interface ControlPreviewButtonProps {
    controlId: string;
    validateForm: ValidateControlViewForm;
}
function ControlPreviewButton({
    controlId,
    validateForm,
}: ControlPreviewButtonProps) {
    const { t } = useTranslation();
    const { mutateAsync: mutateCreatePreview, status } =
        useLegacyControlPreviewMutation();

    const onCreatePreview = async () => {
        const values = await validateForm();
        if (values === null) {
            emitNotification(
                "warning",
                t("CreateControl_Submit_ValidationError")
            );
            return;
        }
        await mutateCreatePreview({
            data: toReducedLegacyControl(values) as LegacyControl,
        });
        const url = `${LegacyRoute.CONTROL_PREVIEW}?controlId=${controlId}`;
        window.open(url);
    };

    return (
        <Button
            size="large"
            onClick={() => void onCreatePreview()}
            loading={status === "pending"}
        >
            {t("CreateControl_PreviewControl")}
        </Button>
    );
}

// Three action buttons don't fit the InformationModal's 400px default.
const SUBMIT_CONFIRMATION_WIDTH = 520;

interface SubmitConfirmationProps {
    onCancelClick: () => void;
    onSubmitClick: ControlSubmitCallback;
    controlCount: number | null;
    sendImmediately: boolean;
    alteredTriggeredControlCount: number;
}
export function SubmitConfirmation({
    onCancelClick,
    onSubmitClick,
    controlCount,
    sendImmediately,
    alteredTriggeredControlCount,
}: SubmitConfirmationProps) {
    const { t } = useTranslation();
    const hasMissingControls = Boolean(controlCount && controlCount > 0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const handleClick = (type: ControlSubmitType) => {
        setIsSubmitting(true);
        onSubmitClick(type).catch(() => {
            setIsSubmitting(false);
        });
    };

    const button = (
        <Button
            key="saveNoSend"
            onClick={() => handleClick("noSend")}
            variant="default"
            disabled={sendImmediately}
        >
            {t("CreateControl_Confirm_NoNewControls")}
        </Button>
    );

    const tooltip = (
        <>
            <Tooltip
                title={t("CreateControl_NoSendOnSendImmediately")}
                className={theme.buttonTooltip}
            >
                {button}
            </Tooltip>
        </>
    );

    const footer = (
        <>
            <Button
                key="cancel"
                onClick={onCancelClick}
                variant="default"
                disabled={isSubmitting}
            >
                {t("Cancel")}
            </Button>
            {hasMissingControls && (sendImmediately ? tooltip : button)}
            <Button
                key="save"
                onClick={() => handleClick("send")}
                variant="primary"
                loading={isSubmitting}
            >
                {hasMissingControls
                    ? t("CreateControl_Confirm_SaveAndSend")
                    : t("CreateControl_Confirm")}
            </Button>
        </>
    );

    const text = hasMissingControls
        ? t("CreateControl_Confirm_MissingControls", {
              controlCount: String(controlCount),
          })
        : t("CreateControl_Confirm_NoMissingControls");
    const open = controlCount !== null;

    return (
        <InformationModal
            open={open}
            onClose={onCancelClick}
            type="info"
            icon="save"
            title={
                hasMissingControls
                    ? t("CreateControl_ConfirmSaveAndSend")
                    : t("CreateControl_ConfirmSave")
            }
            footer={footer}
            width={SUBMIT_CONFIRMATION_WIDTH}
        >
            {alteredTriggeredControlCount > 0 && (
                <Paragraph>
                    {t("CreateControl_Confirm_AltersTriggeredControls", {
                        count: alteredTriggeredControlCount,
                    })}
                </Paragraph>
            )}
            <Text>{text}</Text>
        </InformationModal>
    );
}

export type ControlSubmitType = "send" | "noSend";
export type ControlPageType = "create" | "update";
export type ControlSubmitCallback = (type: ControlSubmitType) => Promise<void>;
export type ValidateControlViewForm =
    () => Promise<ControlViewFormState | null>;
interface ControlSubmitButtonProps {
    onSubmitClick: ControlSubmitCallback;
    submitLabel: string;
    validateForm: ValidateControlViewForm;
    pageType: ControlPageType;
    sendImmediately: boolean;
    alteredTriggeredControlCount: number;
    disabled: boolean;
}
function ControlSubmitButton({
    submitLabel,
    onSubmitClick,
    validateForm,
    pageType,
    sendImmediately,
    alteredTriggeredControlCount,
    disabled,
}: ControlSubmitButtonProps) {
    const { t } = useTranslation();
    const [missingControlResults, setMissingControlResults] = useState<
        number | null
    >(null);

    const { mutateAsync: mutateConfirmationData, status } =
        useLegacyControlConfirmationDataMutation();
    const onSubmitFormClick = async () => {
        const values = await validateForm();
        if (values === null) {
            emitNotification(
                "warning",
                t("CreateControl_Submit_ValidationError")
            );
            return;
        }
        // Ideally this should be a query, but we use it after performing programmatic validation
        const res = await mutateConfirmationData({
            data: toReducedLegacyControlForConfirmationData(
                values,
                pageType === "create",
                sendImmediately
            ) as LegacyConfirmationControlViewModel,
        });
        setMissingControlResults(res.data.MissingControlResults);
    };

    return (
        <>
            <SubmitConfirmation
                onSubmitClick={onSubmitClick}
                onCancelClick={() => setMissingControlResults(null)}
                controlCount={missingControlResults}
                sendImmediately={sendImmediately}
                alteredTriggeredControlCount={alteredTriggeredControlCount}
            />
            <Button
                onClick={() => void onSubmitFormClick()}
                size="large"
                variant="primary"
                loading={status === "pending"}
                disabled={disabled}
            >
                {submitLabel}
            </Button>
        </>
    );
}

interface ControlPageWrapperProps {
    children: React.ReactNode;
    controlId: string;
    controlProgramId: number | null;
    onSubmitClick: ControlSubmitCallback;
    onCancel: () => void;
    validateForm: ValidateControlViewForm;
    formDirty: boolean;
    submitLabel: string;
    titleLabel: string;
    submitDisabled: boolean;
    pageType: ControlPageType;
    sendImmediately: boolean;
    alteredTriggeredControlCount: number;
}
function ControlPageWrapper({
    children,
    controlId,
    validateForm,
    onSubmitClick,
    onCancel,
    formDirty,
    controlProgramId,
    submitLabel,
    titleLabel,
    submitDisabled,
    pageType,
    sendImmediately,
    alteredTriggeredControlCount,
}: ControlPageWrapperProps) {
    const { data: controlProgramOverviews } = useControlProgramsQuery();
    const controlPrograms = controlProgramOverviews?.content;

    const controlProgram = controlPrograms?.find(
        (dto) => dto.resource.controlProgram.id === controlProgramId
    );
    const controlProgramResource = controlProgram?.resource;
    const appBarTitle = controlProgramResource
        ? {
              title: titleLabel,
              breadCrumbs: [
                  {
                      label: controlProgramResource.controlProgram.name,
                      href: buildControlProgramViewUrl(
                          controlProgramResource.controlProgram.id
                      ),
                  },
              ],
          }
        : titleLabel;

    const anchorLabels = useControlViewAnchorLabels();
    const anchors = CONTROL_ANCHORS.map((anchor) => ({
        elementId: anchor,
        label: anchorLabels[anchor],
    }));

    return (
        <OrgApplicationBar
            title={appBarTitle}
            navigation={{
                type: "Anchors",
                anchors,
            }}
        >
            <div className={theme.formFooterWrapper}>
                <div>{children}</div>
                <StickyFooter>
                    <ControlSubmitButton
                        validateForm={validateForm}
                        submitLabel={submitLabel}
                        onSubmitClick={onSubmitClick}
                        pageType={pageType}
                        sendImmediately={sendImmediately}
                        alteredTriggeredControlCount={
                            alteredTriggeredControlCount
                        }
                        disabled={submitDisabled}
                    />
                    <ControlPreviewButton
                        controlId={controlId}
                        validateForm={validateForm}
                    />
                    <ControlCancelButton
                        shouldWarn={formDirty}
                        onCancel={onCancel}
                    />
                </StickyFooter>
            </div>
        </OrgApplicationBar>
    );
}

export { ControlPageWrapper };
